const SCROLLING_CLASS = 'is-scrolling';
const HIDE_DELAY_MS = 800;

/**
 * Reveals scrollbars while the user is actively scrolling by toggling the
 * `is-scrolling` class on the scrolled element. Works together with the
 * global auto-hiding scrollbar rules in src/styles.scss.
 *
 * Uses a single delegated capture-phase listener (scroll events do not
 * bubble), so it covers every current and future scrollable element without
 * per-element listeners.
 *
 * @returns a teardown function that removes the listener.
 */
export function initScrollbarReveal(doc: Document = document): () => void {
  const hideTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>();

  const onScroll = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    target.classList.add(SCROLLING_CLASS);
    const pendingTimer = hideTimers.get(target);
    if (pendingTimer !== undefined) {
      clearTimeout(pendingTimer);
    }
    hideTimers.set(
      target,
      setTimeout(() => {
        target.classList.remove(SCROLLING_CLASS);
        hideTimers.delete(target);
      }, HIDE_DELAY_MS)
    );
  };

  doc.addEventListener('scroll', onScroll, { capture: true, passive: true });
  return () => doc.removeEventListener('scroll', onScroll, { capture: true });
}
